import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  /** Stack above another open modal (0 = default, 1 = nested edit, etc.) */
  stackPriority = 0,
}) => {
  const overlayZ = 9998 + stackPriority * 2;
  const panelZ = 9999 + stackPriority * 2;
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-full mx-2 sm:mx-4',
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{ zIndex: overlayZ }}
          />

          {/* Modal */}
          <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: panelZ }}>
            <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 50 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className={`relative w-full ${sizes[size]} bg-white rounded-2xl sm:rounded-3xl shadow-2xl border-2 border-gray-100 overflow-hidden`}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 border-b-2 border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-extrabold text-gray-900">{title}</h3>
                  {showCloseButton && (
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={onClose}
                      className="p-2 sm:p-2.5 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all"
                    >
                      <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </motion.button>
                  )}
                </div>

                {/* Content */}
                <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 max-h-[70vh] overflow-y-auto">
                  {children}
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;

